import React, { useState, useEffect, useCallback } from 'react';
import { Table, Spinner, Modal, Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getKnockoutWinner, hasPenalties, isWalkover } from '../utils/CupResult';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GroupTable = ({ groupName, teams }) => {
    const navigate = useNavigate();
    return (
        <div className="mb-4">
            <h5 className="text-center">Grupa {groupName}</h5>
            <div style={{ overflowX: 'auto', width: '100%', maxWidth: 700, marginLeft: 'auto', marginRight: 'auto' }}>
                <Table bordered hover className="sticky-col-2" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
                <thead>
                    <tr>
                        <th>#</th>
                        <th></th>
                        <th>Drużyna</th>
                        <th>Pkt.</th>
                        <th>M</th>
                        <th>W</th>
                        <th>R</th>
                        <th>P</th>
                        <th>B</th>
                        <th>RB</th>
                    </tr>
                </thead>
                <tbody>
                    {teams.map((team, idx) => (
                        <tr key={team.team_id} onClick={() => navigate(`/zespol/${team.team_id}`)} style={{ cursor: 'pointer' }} className={idx < 2 ? 'table-success' : ''}>
                            <td style={{ textAlign: 'center' }}>{team.position}</td>
                            <td style={{ textAlign: 'center', padding: '8px 4px' }}>
                                {team.logo_filename && (
                                    <img
                                        src={`/logos/${team.logo_filename}`}
                                        alt={team.team_name}
                                        style={{ width: 20, height: 20, objectFit: 'cover' }}
                                        onError={(e) => (e.currentTarget.style.display = 'none')}
                                    />
                                )}
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                                {team.team_name}
                            </td>
                            <td style={{ textAlign: 'center' }}><strong>{team.points}</strong></td>
                            <td style={{ textAlign: 'center' }}>{team.matches_played}</td>
                            <td style={{ textAlign: 'center' }}>{team.wins}</td>
                            <td style={{ textAlign: 'center' }}>{team.draws}</td>
                            <td style={{ textAlign: 'center' }}>{team.losses}</td>
                            <td style={{ textAlign: 'center' }}>{team.goals_for}:{team.goals_against}</td>
                            <td style={{ textAlign: 'center' }}>{team.goals_balance}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            </div>
        </div>
    );
};

const KnockoutBracket = ({ matches, isAuthenticated, onEditMatch }) => {
    const navigate = useNavigate();
    if (!matches || matches.length === 0) return null;

    // Group matches by stage, order rounds from earliest to final
    const stageOrder = ['1/32 finału', '1/16 finału', '1/8 finału', 'ćwierćfinał', 'półfinał', 'finał'];
    const stageMap = new Map();
    matches.forEach(m => {
        if (!stageMap.has(m.stage)) stageMap.set(m.stage, []);
        stageMap.get(m.stage).push(m);
    });
    const rounds = [...stageMap.entries()]
        .sort(([a], [b]) => stageOrder.indexOf(a) - stageOrder.indexOf(b))
        .map(([, ms]) => ms);

    const matchWidth = 160;
    const matchHeight = 60;
    const matchGap = 20;
    const roundGap = 80;
    const padding = 20;

    const totalRounds = rounds.length;
    const maxMatchesInFirstRound = rounds[0]?.length || 1;
    const svgWidth = totalRounds * (matchWidth + roundGap) + padding * 2;
    const svgHeight = maxMatchesInFirstRound * (matchHeight + matchGap) + padding * 2;

    // Assign y positions to each match based on round
    const matchPositions = {};
    rounds.forEach((round, roundIdx) => {
        const x = padding + roundIdx * (matchWidth + roundGap);
        const totalHeight = round.length * (matchHeight + matchGap) - matchGap;
        const startY = (svgHeight - totalHeight) / 2;
        round.forEach((match, i) => {
            const y = startY + i * (matchHeight + matchGap);
            matchPositions[match.id] = { x, y };
        });
    });

    return (
        <div style={{ width: '100%', maxWidth: 700, marginLeft: 'auto', marginRight: 'auto' }}>
            <h5 className="mb-3 text-center">Faza pucharowa</h5>
            <div style={{ overflowX: 'auto', width: '100%', maxWidth: 700, marginLeft: 'auto', marginRight: 'auto' }}>
                <svg width={svgWidth} height={svgHeight} style={{ display: 'block' }}>
                    {/* Connector lines */}
                    {matches.map(match => {
                        if (!match.next_match_id || !matchPositions[match.id] || !matchPositions[match.next_match_id]) return null;
                        const from = matchPositions[match.id];
                        const to = matchPositions[match.next_match_id];
                        const x1 = from.x + matchWidth;
                        const y1 = from.y + matchHeight / 2;
                        const x2 = to.x;
                        const y2 = to.y + matchHeight / 2;
                        const mx = (x1 + x2) / 2;
                        return (
                            <path
                                key={`line-${match.id}`}
                                d={`M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`}
                                fill="none"
                                stroke="#aaa"
                                strokeWidth="1.5"
                            />
                        );
                    })}

                    {/* Match boxes */}
                    {matches.map(match => {
                        const pos = matchPositions[match.id];
                        if (!pos) return null;
                        const { x, y } = pos;
                        const homeText = match.home_team || '—';
                        const awayText = match.away_team || '—';
                        const pens = hasPenalties(match);
                        const homeScore = match.home_goals !== null
                            ? (pens ? `${match.home_goals} (${match.home_penalties})` : match.home_goals)
                            : '';
                        const awayScore = match.away_goals !== null
                            ? (pens ? `${match.away_goals} (${match.away_penalties})` : match.away_goals)
                            : '';
                        const winner = getKnockoutWinner(match);
                        const homeWeight = winner === 'home' ? 'bold' : 'normal';
                        const awayWeight = winner === 'away' ? 'bold' : 'normal';
                        const homeFill = winner && winner !== 'home' ? '#999' : '#333';
                        const awayFill = winner && winner !== 'away' ? '#999' : '#333';
                        return (
                            <g key={match.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/mecz/${match.id}`)}>
                                <rect x={x} y={y} width={matchWidth} height={matchHeight} rx={4} fill="white" stroke="#ccc" strokeWidth="1" />
                                {/* Stage label */}
                                <text x={x + matchWidth / 2} y={y - 4} textAnchor="middle" fontSize="10" fill="#888">{match.stage}{isWalkover(match) ? ' · walkower' : ''}</text>
                                {/* Divider */}
                                <line x1={x} y1={y + matchHeight / 2} x2={x + matchWidth} y2={y + matchHeight / 2} stroke="#eee" strokeWidth="1" />
                                {/* Home team */}
                                <text x={x + 6} y={y + matchHeight / 2 - 8} fontSize="11" fill={homeFill} fontWeight={homeWeight} dominantBaseline="middle">{homeText}</text>
                                {homeScore !== '' && <text x={x + matchWidth - 6} y={y + matchHeight / 2 - 8} fontSize="11" fill={homeFill} fontWeight={homeWeight} textAnchor="end" dominantBaseline="middle">{homeScore}</text>}
                                {/* Away team */}
                                <text x={x + 6} y={y + matchHeight / 2 + 8} fontSize="11" fill={awayFill} fontWeight={awayWeight} dominantBaseline="middle">{awayText}</text>
                                {awayScore !== '' && <text x={x + matchWidth - 6} y={y + matchHeight / 2 + 8} fontSize="11" fill={awayFill} fontWeight={awayWeight} textAnchor="end" dominantBaseline="middle">{awayScore}</text>}
                                {/* Edit button for authenticated users */}
                                {isAuthenticated && onEditMatch && (
                                    <g style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); onEditMatch(match); }}>
                                        <rect x={x + matchWidth - 20} y={y + 2} width={18} height={18} rx={3} fill="#e8f0fe" stroke="#aac" strokeWidth="0.5" />
                                        <text x={x + matchWidth - 11} y={y + 13} fontSize="11" textAnchor="middle" fill="#336">✏</text>
                                    </g>
                                )}
                            </g>
                        );
                    })}
                </svg>
            </div>
        </div>
    );
};

const CupView = ({ season }) => {
    const { isAuthenticated } = useAuth();
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({ id: null, home_team_id: '', away_team_id: '', date: '' });
    const [editError, setEditError] = useState(null);
    const [teams, setTeams] = useState([]);
    const [cupData, setCupData] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchCupData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${BACKEND_URL}/tables/cup?season=${season}`);
            setCupData(response.data);
        } catch {
            setCupData(null);
        } finally {
            setLoading(false);
        }
    }, [season]);

    useEffect(() => {
        fetchCupData();
    }, [fetchCupData]);

    const handleEditMatch = async (match) => {
        setEditError(null);
        try {
            const response = await axios.get(`${BACKEND_URL}/teams?season=${season}`);
            setTeams(response.data);
            setEditForm({
                id: match.id,
                home_team_id: match.home_team_id || '',
                away_team_id: match.away_team_id || '',
                date: match.date ? match.date.slice(0, 16) : ''
            });
            setShowEditModal(true);
        } catch {
            setEditError('Błąd podczas pobierania drużyn.');
        }
    };

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditForm(prev => ({ ...prev, [name]: value }));
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setEditError(null);
        try {
            await axios.put(`${BACKEND_URL}/matches/update/${editForm.id}`, {
                home_team_id: editForm.home_team_id || null,
                away_team_id: editForm.away_team_id || null,
                date: editForm.date || null
            }, { withCredentials: true });
            setShowEditModal(false);
            fetchCupData();
        } catch {
            setEditError('Błąd podczas edycji meczu.');
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Ładowanie...</span>
                </Spinner>
            </div>
        );
    }

    if (!cupData || (!cupData.groups && !cupData.knockout)) return null;

    const { groups = {}, knockout = [] } = cupData;

    return (
        <div className="d-flex flex-column align-items-center">
            {Object.entries(groups).map(([groupName, teams]) => (
                <div key={groupName} style={{ width: '100%', maxWidth: 700, marginLeft: 'auto', marginRight: 'auto' }}>
                    <GroupTable groupName={groupName} teams={teams} />
                </div>
            ))}
            <KnockoutBracket matches={knockout} isAuthenticated={isAuthenticated} onEditMatch={handleEditMatch} />

            <Modal show={showEditModal} onHide={() => { setShowEditModal(false); setEditError(null); }}>
                <Modal.Header closeButton>
                    <Modal.Title>Edytuj mecz</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editError && <div className="text-danger mb-2">{editError}</div>}
                    <Form onSubmit={handleEditSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Gospodarze</Form.Label>
                            <Form.Select name="home_team_id" value={editForm.home_team_id} onChange={handleEditFormChange}>
                                <option value="">Wybierz drużynę</option>
                                {teams.map(team => (
                                    <option key={team.id} value={team.id}>{team.name}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Goście</Form.Label>
                            <Form.Select name="away_team_id" value={editForm.away_team_id} onChange={handleEditFormChange}>
                                <option value="">Wybierz drużynę</option>
                                {teams.map(team => (
                                    <option key={team.id} value={team.id}>{team.name}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Data i godzina</Form.Label>
                            <Form.Control
                                type="datetime-local"
                                name="date"
                                value={editForm.date}
                                onChange={handleEditFormChange}
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end">
                            <Button variant="secondary" className="me-2" onClick={() => setShowEditModal(false)}>Anuluj</Button>
                            <Button variant="primary" type="submit">Zapisz</Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default CupView;
